// Core types of the game engine. The engine is framework-free: no React, no DOM.
// Rules and behaviour: .ai/game-rules.md (as the legacy code implements them) and the plan's bug fixes.

export type Suit = 'c' | 'd' | 'h' | 's';
export type Rank = '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | 'J' | 'Q' | 'K' | 'A';
/** `Kh`, `10c`: rank + suit letter. Two-deck games have every id twice. */
export type CardId = `${Rank}${Suit}`;
/**
 * Mahjong tiles (rules/mahjong.ts): circles `p1`..`p9`, bamboo `b1`..`b9`, characters `m1`..`m9`, the winds `we ws ww wn`,
 * the dragons `dr dg dw` (red, green, white), the flowers `f1`..`f4` and the seasons `s1`..`s4`.
 */
export type TileId = `${'p' | 'b' | 'm'}${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9}` | `w${'e' | 's' | 'w' | 'n'}` | `d${'r' | 'g' | 'w'}` | `${'f' | 's'}${1 | 2 | 3 | 4}`;
/** Index of a card in the base deck order (unique even with duplicate ids). */
export type CardKey = number;

export interface Card {
  readonly key: CardKey;
  readonly id: CardId | TileId;
  /** A Mahjong tile has no rank and suit: `A` and `c` stand in (no rule of a card game reads them there). */
  readonly rank: Rank;
  readonly suit: Suit;
}

export const GAME_IDS = [
  'klondike:turn-one',
  'klondike:turn-three',
  'double-klondike:turn-one',
  'double-klondike:turn-three',
  'spider:one-suit',
  'spider:two-suits',
  'spider:four-suits',
  'forty-thieves',
  'freecell',
  'pyramid',
  'mahjong',
  'mahjong-connect',
  'hearts',
  'makao',
  'three-five-eight',
  'thousand',
  'kulki',
] as const;
export type GameId = (typeof GAME_IDS)[number];

export type PileRef =
  | { readonly kind: 'stock' }
  | { readonly kind: 'waste' }
  | { readonly kind: 'foundation'; readonly index: number }
  | { readonly kind: 'tableau'; readonly index: number }
  /** FreeCell's free cells (one card each); no other game has them. */
  | { readonly kind: 'cell'; readonly index: number };

/** A card in a pile: `index` counts from the bottom (0) to the top. */
export interface CardRef {
  readonly pile: PileRef;
  readonly index: number;
}

export type Scoring = 'standard' | 'timed';
/** Legacy semantics: anything that is not `off` means on (game.js:263). */
export type AutoMoveOption = 'when_won' | 'off';

/** Every pile is ordered bottom first: index 0 = bottom, last = top (the playable card). */
export interface Piles {
  readonly stock: readonly CardKey[];
  readonly waste: readonly CardKey[];
  readonly foundations: ReadonlyArray<readonly CardKey[]>;
  readonly tableau: ReadonlyArray<readonly CardKey[]>;
  /** FreeCell's free cells, 0 or 1 card each; empty (no cells) in the other games. */
  readonly cells: ReadonlyArray<readonly CardKey[]>;
}

/** One undo step: the whole position before an undoable action. */
export interface Snapshot {
  readonly piles: Piles;
  readonly faceUp: readonly boolean[];
  readonly score: number;
  readonly moves: number;
  /** Game time when the action was made; the undo penalty rule uses it. */
  readonly seconds: number;
  readonly recycles: number;
  readonly wasteShown: number;
  /** Mahjong: hints used so far (an undo keeps their penalty, reducer.ts undo). */
  readonly hints?: number;
}

export interface GameOptions {
  readonly scoring: Scoring;
  readonly autoFlip: boolean;
  readonly autoMove: AutoMoveOption;
}

/** The pure game position. Changed only by the reducer. */
export interface GameState {
  readonly game: GameId;
  readonly deck: readonly Card[];
  /** The suits the deck was built from (Spider one/two suits use a subset). */
  readonly suits: readonly Suit[];
  /** The shuffle pattern of this deal (legacy wire format, rewrite-notes 2.10); Mahjong's ends with the layout. */
  readonly pattern: readonly number[];
  /** "Retry game": the same pattern dealt again; sent as prev_deal=1 with a win. */
  readonly isRetry: boolean;
  /** Fixed when the deal is made; the Options value applies from the next deal. */
  readonly scoring: Scoring;
  readonly autoFlip: boolean;
  readonly piles: Piles;
  readonly faceUp: readonly boolean[];
  readonly score: number;
  readonly moves: number;
  readonly seconds: number;
  readonly timerRunning: boolean;
  readonly recycles: number;
  /**
   * How many top waste cards are shown. Turn-three shows the cards of the LAST stock turn (fanned);
   * after a card is played the fan refills from below, up to 3 (legacy KL-S03). Turn-one shows 1.
   */
  readonly wasteShown: number;
  readonly history: readonly Snapshot[];
  /** Set by the winning action; the history is frozen from then on. */
  readonly won: boolean;
  /** Mahjong: how many times the hint was used in this deal (each costs HINT_PENALTY points, kept by an undo). */
  readonly hints?: number;
  /** Mahjong: the layout of this deal (rules/mahjong.ts MAHJONG_LAYOUTS), kept to the end of the game; 0 elsewhere. */
  readonly layout: number;
}

/**
 * What the board is doing. `dealing`, `busy` (animation, auto-move) and `paused` are set by the
 * controller; `won` follows `GameState.won`.
 */
export type Status = 'dealing' | 'idle' | 'busy' | 'paused' | 'won';

export type MoveResult = 'moved' | 'rejected' | 'busy';

/** Which score-table entry a successful action used (for tests and the view). */
export type ScoreKind =
  | 'waste_to_foundation'
  | 'waste_to_tableau'
  | 'tableau_to_foundation'
  | 'tableau_to_tableau'
  | 'foundation_to_tableau'
  | 'foundation_to_foundation'
  /** Pyramid: two cards that make 13 leave together; a King leaves alone. */
  | 'pair'
  | 'king'
  | 'none';
